import { isDivertedMethod } from "../constants/wasteTaxonomy";

/**
 * Pure helpers for the waste dashboard page: filter persistence, request bodies, and
 * chart/KPI series derived from the API's WasteDashboardSummary. No React, no fetch.
 */

export const FILTER_KEYS = ["year", "site", "legal", "business", "country", "category", "method"];
export const STORAGE_PREFIX = "waste";

export const COLORS = {
  diverted: "#407775",
  disposed: "#C9A227",
  emissions: "#494949",
  hazardous: "#B5483F",
  nonHazardous: "#6FA8A3",
  muted: "#9AA5B1",
};

export function emptySelection() {
  return FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: [] }), {});
}

export function readStoredSelection(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  const sel = emptySelection();
  if (!storage) return sel;
  FILTER_KEYS.forEach((k) => {
    try {
      const raw = storage.getItem(`${STORAGE_PREFIX}${k}`);
      const parsed = raw ? JSON.parse(raw) : [];
      sel[k] = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      sel[k] = [];
    }
  });
  return sel;
}

export function writeStoredSelection(sel, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  if (!storage) return;
  FILTER_KEYS.forEach((k) => {
    try {
      storage.setItem(`${STORAGE_PREFIX}${k}`, JSON.stringify(sel[k] || []));
    } catch (e) {
      /* storage may be unavailable */
    }
  });
}

/** Selection → WasteFilterParameters body (camelCase, as the other dashboards post). */
export function toFilterBody(sel, companyId) {
  const s = sel || {};
  return {
    companyId: companyId || null,
    year: s.year || [],
    site: s.site || [],
    legalEntity: s.legal || [],
    businessUnit: s.business || [],
    country: s.country || [],
    wasteCategory: s.category || [],
    disposalMethod: s.method || [],
  };
}

const yearDesc = (a, b) => Number(b) - Number(a);
const alpha = (a, b) => String(a).localeCompare(String(b));

/** Dictionary<string,bool> from FilterForWasteDashboard → MultiSelect options. */
export function optionsFromFilterResponse(dict, kind = "text") {
  if (!dict || typeof dict !== "object") return [];
  const keys = Object.keys(dict).sort(kind === "year" ? yearDesc : alpha);
  return keys.map((k) => ({ label: k, value: k, disabled: dict[k] === false }));
}

/** Drop selected values the cascade no longer offers, so a stale selection cannot empty the dashboard. */
export function pruneSelection(sel, options) {
  const out = { ...emptySelection(), ...(sel || {}) };
  const map = { year: "year", site: "site", legal: "legalEntity", business: "businessUnit", country: "country", category: "wasteCategory", method: "disposalMethod" };
  FILTER_KEYS.forEach((k) => {
    const dict = options && options[map[k]];
    if (!dict) return;
    out[k] = (out[k] || []).filter((v) => Object.prototype.hasOwnProperty.call(dict, v));
  });
  return out;
}

// Keep whole numbers for values >= 1; preserve decimals for 0 < |v| < 1 so small emissions are not shown as 0.
export function toMetricNumber(value) {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) >= 1) return Math.round(value * 10) / 10;
  const twoDp = Number(value.toFixed(2));
  return twoDp !== 0 ? twoDp : Number(value.toPrecision(2));
}

export function formatNumber(value, maxFractionDigits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: n !== 0 && Math.abs(n) < 1 ? 3 : maxFractionDigits });
}

export function formatPercent(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%` : "0%";
}

export function hasAnyData(summary) {
  const t = summary && summary.totals;
  return !!t && (t.rowCount > 0 || t.pendingReviewCount > 0);
}

/** Headline cards. `change` is the percent change vs previous year when the API supplied it. */
export function kpiCards(summary) {
  const t = (summary && summary.totals) || {};
  const ey = (summary && summary.emissionsYearwise) || {};
  const ty = (summary && summary.tonnesYearwise) || {};
  const hasYoY = Object.prototype.hasOwnProperty.call(ey, "currentYear");
  return [
    {
      key: "emissions",
      title: "Waste emissions (tCO2e)",
      value: formatNumber(t.emissionsTCO2e, 3),
      change: hasYoY ? ey.Change : null,
      previous: hasYoY ? formatNumber(ey.previousYear, 3) : null,
      sub: hasYoY ? `${formatNumber(ey.previousYear, 3)} tCO2e previous year` : null,
    },
    {
      key: "tonnes",
      title: "Waste generated (tonnes)",
      value: formatNumber(t.tonnes),
      change: hasYoY ? ty.Change : null,
      previous: hasYoY ? formatNumber(ty.previousYear) : null,
      sub: `${formatNumber(t.hazardousTonnes)} t hazardous · ${formatNumber(t.nonHazardousTonnes)} t non-hazardous`,
    },
    {
      key: "diversion",
      title: "Diverted from disposal",
      value: formatPercent(t.diversionRate),
      sub: `${formatNumber(t.divertedTonnes)} t recovered · ${formatNumber(t.disposedTonnes)} t to disposal (GRI 306-4/5)`,
    },
    {
      key: "hazardous",
      title: "Hazardous share",
      value: formatPercent(t.hazardousShare),
      sub: `${formatNumber(t.hazardousTonnes)} t of ${formatNumber(t.tonnes)} t`,
    },
    {
      key: "review",
      title: "Pending review",
      value: String(t.pendingReviewCount || 0),
      sub: t.pendingReviewCount ? `${formatNumber(t.pendingReviewTonnes)} t without an emission factor` : "All rows have a factor",
      attention: (t.pendingReviewCount || 0) > 0,
    },
    {
      key: "proxy",
      title: "Proxy factors used",
      value: String(t.proxyFactorRows || 0),
      sub: t.proxyFactorRows ? "Closest available category; lower confidence" : "No proxies in the selection",
      attention: (t.proxyFactorRows || 0) > 0,
    },
  ];
}

export function changeLabel(change) {
  const n = Number(change);
  if (!Number.isFinite(n) || n === 0) return { text: "no change", direction: "flat" };
  return { text: `${n > 0 ? "▲" : "▼"} ${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 1 })}% vs previous year`, direction: n > 0 ? "up" : "down" };
}

/** Donut by category: [{ name, y, color }] */
export function categoryDonutSeries(byCategory) {
  return Object.entries(byCategory || {})
    .map(([name, b]) => ({
      name,
      y: toMetricNumber(b.tonnes),
      emissions: toMetricNumber(b.emissionsTCO2e),
      color: /hazard/i.test(name) && !/non/i.test(name) ? COLORS.hazardous : COLORS.nonHazardous,
    }))
    .filter((p) => p.y > 0)
    .sort((a, b) => b.y - a.y);
}

/** Monthly stacked columns (diverted / disposed tonnes) with an emissions line. */
export function monthlySeries(monthly) {
  const points = Array.isArray(monthly) ? monthly : [];
  return {
    categories: points.map((m) => m.label),
    diverted: points.map((m) => toMetricNumber(m.divertedTonnes)),
    disposed: points.map((m) => toMetricNumber(m.disposedTonnes)),
    emissions: points.map((m) => toMetricNumber(m.emissionsTCO2e)),
  };
}

/** Horizontal bars by disposal method, colour by disposition, largest first. */
export function disposalMethodBarSeries(byDisposalMethod) {
  const rows = Object.entries(byDisposalMethod || {})
    .map(([name, b]) => ({
      name,
      tonnes: toMetricNumber(b.tonnes),
      emissions: toMetricNumber(b.emissionsTCO2e),
      disposition: b.disposition || (isDivertedMethod(name) ? "Diverted" : "Disposed"),
    }))
    .sort((a, b) => b.tonnes - a.tonnes);
  return {
    categories: rows.map((r) => r.name),
    data: rows.map((r) => ({ y: r.tonnes, emissions: r.emissions, disposition: r.disposition, color: r.disposition === "Diverted" ? COLORS.diverted : COLORS.disposed })),
  };
}

/** Top N waste types by tonnes with their emissions. */
export function topWasteTypes(byWasteType, n = 8) {
  const rows = Object.entries(byWasteType || {})
    .map(([name, b]) => ({ name, tonnes: toMetricNumber(b.tonnes), emissions: toMetricNumber(b.emissionsTCO2e), category: b.category }))
    .sort((a, b) => b.tonnes - a.tonnes)
    .slice(0, n);
  return { categories: rows.map((r) => r.name), tonnes: rows.map((r) => r.tonnes), emissions: rows.map((r) => r.emissions), rows };
}

/** Method mix by row count: [{ name, y }] */
export function methodMixSeries(methodMix) {
  return Object.entries(methodMix || {})
    .map(([name, b]) => ({ name, y: b.rowCount || 0, tonnes: toMetricNumber(b.tonnes) }))
    .filter((p) => p.y > 0)
    .sort((a, b) => b.y - a.y);
}

export function dataQualitySeries(dataQuality) {
  const order = ["High", "Medium", "Medium (proxy)", "Not calculated"];
  return Object.entries(dataQuality || {})
    .map(([name, y]) => ({ name, y }))
    .filter((p) => p.y > 0)
    .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
}

/** "AR5 ×6", "AR6 ×1" chips so a mixed-basis total is visible. */
export function gwpChips(gwpBases) {
  return Object.entries(gwpBases || {})
    .sort((a, b) => b[1] - a[1])
    .map(([basis, count]) => `${basis} ×${count}`);
}

export function editionChips(editions) {
  return Object.entries(editions || {})
    .sort((a, b) => b[1] - a[1])
    .map(([edition, count]) => `${edition} ×${count}`);
}
